import { paths } from "config/paths";
import { ReactNode, useEffect, useState } from "react";
import { useMap } from "react-map-gl/maplibre";
import { MapTools as View } from "ui-components";
import { useWebRouter } from "~utils/next-router/use-web-router";

import {
	isToolEnabled,
	useGetCurrentMap,
} from "~map-view/queries/useGetCurrentMap";
import {
	useCurrentMapToolView,
	useCurrentSearchInput,
	useCurrentSearchType,
	useOpenCustomSearchTool,
	useOpenGeolocationTool,
	useOpenMapTool,
	useOpenMeasureTool,
	useSetCurrentSearchType,
} from "~map-view/states";

export const MapTools = () => {
	const router = useWebRouter();

	const openCustomSearchTool = useOpenCustomSearchTool();
	const openMeasureTool = useOpenMeasureTool();
	const openGeolocationTool = useOpenGeolocationTool();
	const openMapTool = useOpenMapTool();
	const currentSearchType = useCurrentSearchType();
	const setCurrentSearchType = useSetCurrentSearchType();
	const currentSearchInput = useCurrentSearchInput();
	const { data: map, isLoading } = useGetCurrentMap();
	const currentMapToolView = useCurrentMapToolView();
	const [isAttributionExpanded, setAttributionExpanded] = useState(false);
	const { default: mapRef } = useMap();
	const [mousePosition, setMousePosition] = useState<{
		lat: ReactNode;
		lng: ReactNode;
	}>({ lat: 0, lng: 0 });

	useEffect(() => {
		if (mapRef) {
			mapRef.on("load", (e) => {
				setMousePosition({
					lat: e.target.getCenter().lat.toFixed(6),
					lng: e.target.getCenter().lng.toFixed(6),
				});
			});
			mapRef.on("mousemove", (e) => {
				setMousePosition({
					lat: e.lngLat.lat.toFixed(6),
					lng: e.lngLat.lng.toFixed(6),
				});
			});
		}
	}, [mapRef]);

	if (isLoading) return null;

	return (
		<View
			showStreetviewMarker={currentMapToolView === "streetview"}
			toolsProps={{
				"map-menu": {
					onClick: () => openMapTool("map-menu"),
				},
				"zoom-in": {
					onClick: () => {
						if (mapRef) mapRef.zoomIn();
					},
				},
				"zoom-out": {
					onClick: () => {
						if (mapRef) mapRef.zoomOut();
					},
				},
				attribution: {
					isExpanded: isAttributionExpanded,
					customContent: "© MangoMap 2024",
					expandedButtonProps: {
						onClick: () => setAttributionExpanded(false),
					},
					collapsedButtonProps: {
						onClick: () => setAttributionExpanded(true),
					},
				},
				search: {
					$enabled: !!map && isToolEnabled({ map, tool: "search" }),
					currentType: currentSearchType,
					currentInput: currentSearchInput,
					onClick: () => openMapTool("search"),
					onTypeChange: setCurrentSearchType,
				},
				"mouse-position": {
					onCopyClick: () => {
						if (!("clipboard" in navigator))
							throw new Error("Clipboard not supported");

						void navigator.clipboard.writeText(
							`${mousePosition.lat}, ${mousePosition.lng}`,
						);
					},
					$enabled: !!map && isToolEnabled({ map, tool: "mousePosition" }),
					position: mousePosition,
				},
				"scale-bar": {
					$enabled: !!map && isToolEnabled({ map, tool: "scaleBar" }),
					value: { km: 3000, mi: 1000 },
				},
				streetview: {
					$enabled: !!map && isToolEnabled({ map, tool: "streetView" }),
					onClick: () => openMapTool("streetview"),
				},
				legend: {
					$enabled: !!map && isToolEnabled({ map, tool: "legend" }),
					onClick: () => openMapTool("legend"),
				},
				measure: {
					$enabled: !!map && isToolEnabled({ map, tool: "measure" }),
					onClick: openMeasureTool,
				},
				"custom-search": {
					$enabled: !!map && isToolEnabled({ map, tool: "customSearch" }),
					onClick: openCustomSearchTool,
				},
				geolocation: {
					$enabled: !!map && isToolEnabled({ map, tool: "geolocation" }),
					onClick: openGeolocationTool,
				},
				print: {
					$enabled: !!map && isToolEnabled({ map, tool: "print" }),
					href: paths.accountName.maps.printPreview(
						router._query.accountName,
						router._query.mapId,
					),
					target: "_blank",
				},
				bookmarks: {
					$enabled: !!map && isToolEnabled({ map, tool: "bookmarks" }),
					onClick: () => openMapTool("bookmarks"),
				},
				query: {
					$enabled: !!map && isToolEnabled({ map, tool: "query" }),
					onClick: () => openMapTool("query"),
				},
				proximity: {
					$enabled: !!map && isToolEnabled({ map, tool: "proximity" }),
					onClick: () => openMapTool("proximity"),
				},
				sketch: {
					$enabled: !!map && isToolEnabled({ map, tool: "sketch" }),
					onClick: () => openMapTool("sketch"),
				},
				"reset-map": {
					$enabled: !!map && isToolEnabled({ map, tool: "resetMap" }),
				},
				"share-map": {
					$enabled: !!map && isToolEnabled({ map, tool: "shareMap" }),
				},
			}}
		/>
	);
};
